import { Building2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';

interface SidebarFooterProps {
  /** Compact icon-only rendering when the sidebar is collapsed. */
  collapsed?: boolean;
}

/**
 * D-merge-strategy (zz-merge-strategy.html §4): the property switcher — the one thing worth
 * keeping from the retired bottom status ribbon — moves to a fixed footer on the sidebar, so
 * property identity stays visible at a glance and the switch is one click.
 */
export function SidebarFooter({ collapsed }: Readonly<SidebarFooterProps>) {
  const { activeSiteId, sites, setActiveSite } = useAuth();
  const activeSite = sites.find((s) => s.siteId === activeSiteId);

  if (sites.length === 0) return null;

  if (collapsed) {
    return (
      <div
        className="flex shrink-0 items-center justify-center border-t border-white/[0.06] py-2"
        title={activeSite?.name}
      >
        <Building2 className="h-4 w-4 text-white/40" />
      </div>
    );
  }

  return (
    <div
      data-sidebar-footer
      className="flex shrink-0 items-center gap-1.5 border-t border-white/[0.06] px-2.5 py-2 text-white/70"
    >
      <Building2 className="h-3.5 w-3.5 shrink-0 text-white/40" />
      <NativeSelect
        value={activeSiteId ?? ''}
        onChange={(e) => setActiveSite(e.target.value)}
        className="[&_[data-slot=native-select]]:h-auto [&_[data-slot=native-select]]:border-none [&_[data-slot=native-select]]:bg-transparent [&_[data-slot=native-select]]:py-0 [&_[data-slot=native-select]]:pr-4 [&_[data-slot=native-select]]:pl-0 [&_[data-slot=native-select]]:text-xs [&_[data-slot=native-select]]:text-white/70 [&_[data-slot=native-select]]:shadow-none [&_[data-slot=native-select-icon]]:text-white/40"
      >
        {sites.map((site) => (
          <NativeSelectOption key={site.siteId} value={site.siteId} className="text-foreground">
            {site.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </div>
  );
}
